import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Search, Building2, Plus, X, Trash2, Loader2, ChevronDown, MapPin, Hash, Users,
  Receipt, Phone, Mail, Truck, Star, Pencil, Check, MessageCircle, UserCircle2, Briefcase,
  Image as ImageIcon, Upload, CalendarClock, ChevronRight,
} from 'lucide-react'
import LogoEditor from '../components/LogoEditor.jsx'
import { useConfirm } from '../components/ConfirmProvider.jsx'
import { api } from '../lib/api.js'
import {
  CARD, CARD_BASE, PROVINCES, BILLING_RIGHTS, MultiChips, Field, Toast, Combobox,
  STAGE_DOT, STAGE_BADGE, CHANNEL_DOT, FilterButton, ChipPanel, ActiveChip, ConfirmDialog, CreatedByChip,
} from '../components/ui.jsx'
import { BANGKOK, BANGKOK_DISTRICTS, isBangkok, resolveSsoBranch, ssoOptionsForProvince, addCustomBranch, removeCustomBranch, customBranchesFor } from '../lib/ssoArea.js'
import { validatePhone, validateEmail, firstError } from '../lib/validate.js'
import { FollowUpDrawer } from './FollowUps.jsx'

const NEW_CO = Object.freeze({ __new: true })

export default function Companies() {
  const [rows, setRows]   = useState(null)
  const [q, setQ]         = useState('')
  const [provFilter, setProvFilter] = useState([])     // multi-select province filter
  const [onlyWithOps, setOnlyWithOps] = useState(false) // companies that have ≥1 operation
  const [error, setError] = useState(null)
  const [editing, setEditing] = useState(null)
  const [lastViewedId, setLastViewedId] = useState(() => {
    const v = parseInt(sessionStorage.getItem('lastViewedCompanyId') || '', 10)
    return Number.isFinite(v) ? v : null
  })
  const [toast, setToast] = useState(null)
  const [fuEditing, setFuEditing] = useState(null)
  const [lockedCompany, setLockedCompany] = useState(null)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const PAGE_SIZE = 50

  const refresh = () => {
    api.companies.list({
      q,
      province: provFilter,
      only_with_ops: onlyWithOps ? 1 : '',
      limit:  PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    })
      .then((res) => { setRows(res.rows || []); setTotal(res.total || 0) })
      .catch((e) => setError(e.message))
  }
  useEffect(refresh, [q, provFilter, onlyWithOps, page])
  // Any filter/search change resets to page 1.
  useEffect(() => { setPage(1) }, [q, provFilter, onlyWithOps])

  const activeFilterCount = (provFilter.length ? 1 : 0) + (onlyWithOps ? 1 : 0)
  const clearFilters = () => { setProvFilter([]); setOnlyWithOps(false) }
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  return (
    <div className="px-4 py-5 md:px-10 md:py-8 max-w-[1400px] mx-auto">
      <header className="mb-5 md:mb-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div className="flex items-start gap-3">
          <span aria-hidden className="hidden md:block mt-1.5 w-1 h-9 rounded-full bg-gradient-to-b from-amber-400 to-orange-500" />
          <div>
            <h1 className="text-[24px] md:text-[28px] font-bold tracking-tight text-zinc-900 leading-tight">Companies</h1>
            <p className="text-[13px] md:text-[13.5px] text-zinc-500 mt-1">Master directory of all engaged companies.</p>
          </div>
        </div>
        <div className="flex flex-col items-start md:items-end gap-2.5">
          <button
            onClick={() => setEditing(NEW_CO)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-semibold bg-amber-500 text-white hover:bg-amber-600 shadow-sm shadow-amber-500/25 transition-colors"
          >
            <Plus size={14} strokeWidth={2.5} />
            New company
          </button>
          <div className="text-[12.5px] text-zinc-500 tabular-nums inline-flex items-baseline gap-1.5">
            {rows
              ? <><span className="text-[15px] font-semibold text-zinc-900">{rows.length.toLocaleString()}</span><span>companies</span></>
              : 'Loading…'}
          </div>
        </div>
      </header>

      <div className={`${CARD_BASE} relative z-10 mb-4`}>
        <div className="px-3 py-2.5 flex flex-col gap-2 md:flex-row md:items-center md:gap-2">
          <div className="relative w-full md:flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="ค้นหา (ชื่อบริษัท · เลขผู้เสียภาษี)"
              className="w-full pl-9 pr-3 py-2 bg-transparent text-[13.5px] placeholder:text-zinc-400 focus:outline-none"
            />
          </div>

          <div className="hidden md:block h-6 w-px bg-zinc-200" />

          <div className="flex items-center gap-2 flex-wrap">
            <FilterButton icon={MapPin} label="Province" count={provFilter.length}>
              <ChipPanel options={PROVINCES} value={provFilter} onChange={setProvFilter} />
            </FilterButton>

            <button
              type="button"
              onClick={() => setOnlyWithOps((v) => !v)}
              className={
                'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[12px] font-medium transition-colors ring-1 ' +
                (onlyWithOps
                  ? 'bg-amber-50 text-amber-800 ring-amber-200'
                  : 'bg-white text-zinc-600 ring-zinc-200 hover:bg-zinc-50')
              }
              title="Show only companies with at least one operation record"
            >
              <Truck size={13} strokeWidth={2.25} />
              Has operations
            </button>

            {activeFilterCount > 0 && (
              <button onClick={clearFilters}
                className="px-2.5 py-1.5 rounded-md text-[12px] font-medium text-zinc-500 hover:text-zinc-900 hover:bg-zinc-50">
                Clear
              </button>
            )}
          </div>
        </div>

        {activeFilterCount > 0 && (
          <div className="px-3 pb-3 -mt-1 flex flex-wrap items-center gap-1.5">
            {provFilter.map((p) => (
              <ActiveChip key={`pv-${p}`} icon={MapPin} label="Province" value={p}
                onRemove={() => setProvFilter(provFilter.filter((x) => x !== p))} />
            ))}
            {onlyWithOps && (
              <ActiveChip icon={Truck} label="Filter" value="Has operations"
                onRemove={() => setOnlyWithOps(false)} />
            )}
          </div>
        )}
      </div>

      {error && <div className="mb-4 px-4 py-2.5 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-[13px]">{error}</div>}

      <div className={CARD}>
        <table className="w-full table-fixed text-[13px]">
          <colgroup>
            <col />
            <col className="hidden md:table-column md:w-[180px]" />
            <col className="hidden md:table-column md:w-[130px]" />
            <col className="hidden md:table-column md:w-[110px]" />
            <col className="hidden md:table-column md:w-[180px]" />
          </colgroup>
          <thead>
            <tr className="border-b border-zinc-100 text-[11px] uppercase tracking-wider text-zinc-500 font-medium">
              <th className="px-3 md:px-5 py-3 text-left">Name</th>
              <th className="hidden md:table-cell px-5 py-3 text-left">Primary Contact</th>
              <th className="hidden md:table-cell px-5 py-3 text-left">Province</th>
              <th className="hidden md:table-cell px-5 py-3 text-right">Employees</th>
              <th className="hidden md:table-cell px-5 py-3 text-left">Billing</th>
            </tr>
          </thead>
          <tbody>
            {!rows && <tr><td colSpan={5} className="px-5 py-12 text-center text-zinc-400">Loading…</td></tr>}
            {rows && rows.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-12 text-center">
                <Building2 size={20} className="mx-auto text-zinc-300" />
                <div className="mt-2 text-[13px] text-zinc-500">No companies found.</div>
              </td></tr>
            )}
            {rows && rows.map((c) => {
              const isLastViewed = c.id === lastViewedId
              return (
              <tr key={c.id}
                onClick={() => { sessionStorage.setItem('lastViewedCompanyId', String(c.id)); setLastViewedId(c.id); setEditing(c) }}
                className={
                  'border-b border-zinc-100 last:border-b-0 transition-colors cursor-pointer ' +
                  (isLastViewed
                    ? 'bg-amber-50 hover:bg-amber-100/70 ring-1 ring-inset ring-amber-300/40'
                    : 'hover:bg-amber-50/30')
                }>
                <td className="px-3 md:px-5 py-2.5 align-top">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <CompanyLogoAvatar name={c.name} url={c.logo_url || null} size={32} />
                    <span className="font-medium text-zinc-900 truncate" title={c.name}>{c.name}</span>
                    {c.operation_count > 0 && (
                      <OpsBadge count={c.operation_count} lastYear={c.last_operation_year} />
                    )}
                  </div>
                  <div className="md:hidden mt-0.5 text-[12px] text-zinc-500 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    {c.primary_contact_name && <span>{c.primary_contact_name}</span>}
                    {c.province && <><span className="text-zinc-300">·</span><span>{c.province}</span></>}
                    {c.employee_count && <><span className="text-zinc-300">·</span><span className="tabular-nums">{c.employee_count.toLocaleString()} พนักงาน</span></>}
                    {c.billing_rights && (() => {
                      const rights = (() => { try { return JSON.parse(c.billing_rights) } catch { return [] } })()
                      return rights.length > 0 ? (
                        <div className="w-full flex flex-wrap gap-1 mt-0.5">
                          {rights.map((b) => (
                            <span key={b} className="px-1.5 py-0.5 rounded bg-zinc-50 ring-1 ring-zinc-200/70 text-[10.5px] font-medium text-zinc-600">{b}</span>
                          ))}
                        </div>
                      ) : null
                    })()}
                  </div>
                </td>
                <td className="hidden md:table-cell px-5 py-2.5 text-zinc-600 align-top">{c.primary_contact_name || <span className="text-zinc-300">—</span>}</td>
                <td className="hidden md:table-cell px-5 py-2.5 text-zinc-600 align-top">{c.province || <span className="text-zinc-300">—</span>}</td>
                <td className="hidden md:table-cell px-5 py-2.5 text-right text-zinc-700 tabular-nums align-top">{c.employee_count?.toLocaleString() || <span className="text-zinc-300">—</span>}</td>
                <td className="hidden md:table-cell px-5 py-2.5 align-top">
                  {c.billing_rights ? (
                    <div className="flex flex-wrap gap-1">
                      {(JSON.parse(c.billing_rights) || []).map((b) => (
                        <span key={b} className="px-1.5 py-0.5 rounded bg-zinc-50 ring-1 ring-zinc-200/70 text-[11px] font-medium text-zinc-600">{b}</span>
                      ))}
                    </div>
                  ) : <span className="text-zinc-300">—</span>}
                </td>
              </tr>
              )
            })}
          </tbody>
        </table>

        {total > 0 && (
          <div className="px-3 md:px-5 py-3 border-t border-zinc-100 flex items-center justify-between gap-3 text-[12.5px] text-zinc-600">
            <div className="tabular-nums">
              <span className="font-medium text-zinc-900">{(rows?.length ? (page - 1) * PAGE_SIZE + 1 : 0).toLocaleString()}</span>
              {' – '}
              <span className="font-medium text-zinc-900">{((page - 1) * PAGE_SIZE + (rows?.length || 0)).toLocaleString()}</span>
              {' / '}
              <span className="font-medium text-zinc-900">{total.toLocaleString()}</span>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage(1)} disabled={page <= 1}
                className="px-2 py-1 rounded hover:bg-zinc-100 disabled:opacity-30">«</button>
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
                className="px-2 py-1 rounded hover:bg-zinc-100 disabled:opacity-30">‹</button>
              <span className="px-2 tabular-nums">หน้า {page} / {totalPages}</span>
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                className="px-2 py-1 rounded hover:bg-zinc-100 disabled:opacity-30">›</button>
              <button onClick={() => setPage(totalPages)} disabled={page >= totalPages}
                className="px-2 py-1 rounded hover:bg-zinc-100 disabled:opacity-30">»</button>
            </div>
          </div>
        )}
      </div>

      <CompanyDrawer
        company={editing}
        onClose={() => setEditing(null)}
        onSaved={(isNew) => { setEditing(null); refresh(); setToast({ id: Date.now(), message: isNew ? 'Company created' : 'Changes saved' }) }}
        onDeleted={() => { setEditing(null); refresh(); setToast({ id: Date.now(), message: 'Company deleted' }) }}
        onAddFollowUp={(co) => { setLockedCompany(co); setFuEditing({ __new: true }) }}
        onEditFollowUp={(fu, co) => { setLockedCompany(co); setFuEditing(fu) }}
        toastBump={(msg) => setToast({ id: Date.now(), message: msg })}
      />

      <FollowUpDrawer
        followUp={fuEditing}
        lockedCompany={lockedCompany}
        onClose={() => { setFuEditing(null); setLockedCompany(null) }}
        onSaved={(isNew) => {
          setFuEditing(null); setLockedCompany(null)
          // Trigger company drawer refresh if open (handled internally via key)
          setToast({ id: Date.now(), message: isNew ? 'Follow-up created' : 'Changes saved' })
          // Re-open the company drawer so it reloads its follow-ups
          if (editing && !editing.__new) {
            const id = editing.id
            setEditing(null)
            setTimeout(() => api.companies.get(id).then(setEditing).catch(() => {}), 50)
          }
        }}
        onDeleted={() => {
          setFuEditing(null); setLockedCompany(null)
          setToast({ id: Date.now(), message: 'Follow-up deleted' })
          if (editing && !editing.__new) {
            const id = editing.id
            setEditing(null)
            setTimeout(() => api.companies.get(id).then(setEditing).catch(() => {}), 50)
          }
        }}
        onAddAnother={(company) => {
          setLockedCompany({ id: company.id, name: company.name })
          setFuEditing({ __new: true, company_id: company.id, company_name: company.name })
        }}
      />

      <Toast toast={toast} onDone={() => setToast(null)} />
    </div>
  )
}


/* ──────────────────────────────── Company drawer ──────────────────────────────── */

export function CompanyDrawer({ company, onClose, onSaved, onDeleted, onAddFollowUp, onEditFollowUp, zClass = 'z-30' }) {
  const confirm = useConfirm()
  const open  = !!company
  const isNew = company?.__new === true
  const [form, setForm] = useState(null)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [err, setErr] = useState(null)
  const [detail, setDetail] = useState(null)   // full server-fetched record (with contacts + follow_ups)
  const [logoUrl, setLogoUrl] = useState(null) // presigned (lazy)
  const [logoEditorOpen, setLogoEditorOpen] = useState(false)

  // Initialize form: for new companies, seed from caller (lead → company
  // prefills pc_* etc.). For existing companies we MUST wait for the full
  // /api/companies/:id fetch before showing the form — the prop may only
  // carry { id, name } (e.g. when opened from an OperationDetail header),
  // and the list endpoint doesn't return fields like address/official_name/
  // billing_detail/etc. either. If we seeded the form from a partial row,
  // save() would treat the missing fields as empty strings and the API would
  // trim them to NULL — silently wiping data on save.
  useEffect(() => {
    if (!company) return
    setErr(null); setConfirmOpen(false); setLogoUrl(null)
    if (isNew) {
      setForm(emptyForm(company))
      setDetail(null)
    } else {
      setForm(null)
      setDetail(null)
      api.companies.get(company.id).then((d) => {
        setDetail(d)
        setForm(formFromCompany(d))
      }).catch((e) => setErr(e.message))
    }
  }, [company?.id, company?.__new])

  // Lazy-load the presigned logo URL whenever the underlying record reports
  // a logo timestamp. Re-fetches when timestamp changes (after a re-save).
  useEffect(() => {
    if (!detail?.id || !detail.logo_updated_at) { setLogoUrl(null); return }
    let cancelled = false
    api.companies.logo.get(detail.id)
      .then((r) => { if (!cancelled) setLogoUrl(r?.url || null) })
      .catch(() => { if (!cancelled) setLogoUrl(null) })
    return () => { cancelled = true }
  }, [detail?.id, detail?.logo_updated_at])

  async function saveLogo(dataUri) {
    if (!detail?.id) return
    await api.companies.logo.save(detail.id, dataUri)
    setLogoEditorOpen(false)
    // Reload the record to pick up new logo_updated_at, which triggers a fresh
    // presigned URL fetch via the effect above.
    const fresh = await api.companies.get(detail.id)
    setDetail(fresh); setForm(formFromCompany(fresh))
  }
  async function removeLogo() {
    if (!detail?.id) return
    if (!await confirm({ title: 'ลบโลโก้บริษัท', message: 'โลโก้ปัจจุบันจะถูกลบออกจากเซิร์ฟเวอร์', confirmLabel: 'ลบ', tone: 'danger' })) return
    await api.companies.logo.remove(detail.id)
    const fresh = await api.companies.get(detail.id)
    setDetail(fresh); setForm(formFromCompany(fresh))
  }

  useEffect(() => {
    if (!open) return
    const onEsc = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onEsc)
    return () => document.removeEventListener('keydown', onEsc)
  }, [open, onClose])

  const save = async () => {
    setErr(null)
    if (!form.name.trim()) { setErr('Name is required.'); return }
    // Validate email fields only — phone is intentionally free-form text so
    // staff can store anything (โทร 2 เบอร์, ต่อ 12, LINE, etc.).
    const validationErr = firstError(
      validateEmail(form.email,    'Company email'),
      // Primary-contact seed fields are only saved on create — skip otherwise.
      isNew ? validateEmail(form.pc_email, 'Primary contact email') : null,
    )
    if (validationErr) { setErr(validationErr); return }

    const payload = {
      name: form.name.trim(),
      official_company_name: form.official_company_name?.trim() || null,
      authorized_signer_name: form.authorized_signer_name?.trim() || null,
      tax_id: form.tax_id || null,
      address: form.address || null,
      province: form.province || null,
      // District only meaningful for Bangkok; null everywhere else.
      district: isBangkok(form.province) ? (form.district || null) : null,
      employee_count: form.employee_count === '' ? null : parseInt(form.employee_count, 10),
      billing_rights: form.billing_rights,
      billing_detail: form.billing_detail || null,
      sso_district: form.sso_district || null,
      sso_employer_account: form.sso_employer_account || null,
      used_before: form.used_before == null ? null : (form.used_before ? 1 : 0),
    }
    // On create: also seed the primary contact from the company-form's pc_* fields.
    // Name is required for a contact row to be inserted (API silently skips empty names).
    if (isNew) {
      const pcName = (form.pc_name || '').trim()
      if (pcName) {
        payload.initial_contact = {
          name:     pcName,
          fullname: (form.pc_fullname || '').trim() || null,
          phone:    (form.pc_phone    || '').trim() || null,
          email:    (form.pc_email    || '').trim() || null,
          line_id:  (form.pc_line_id  || '').trim() || null,
          role:     (form.pc_role     || '').trim() || null,
        }
      }
    }

    setSaving(true)
    try {
      let created = null
      if (isNew) {
        const r = await api.companies.create(payload)
        created = { id: r?.id, name: r?.name || payload.name }
      } else {
        await api.companies.update(company.id, payload)
      }
      // Pass the freshly-created row back so callers (e.g. the lead → company
      // flow) can link to it without an extra fetch.
      onSaved(isNew, created)
    } catch (e) { setErr(e.message) } finally { setSaving(false) }
  }

  const doDelete = async () => {
    setErr(null); setDeleting(true)
    try { await api.companies.remove(company.id); onDeleted?.(company.id) }
    catch (e) { setErr(e.message) }
    finally { setDeleting(false); setConfirmOpen(false) }
  }

  if (!open) return null

  // While we wait for /api/companies/:id to resolve (we no longer seed from
  // the prop — see useEffect comment above), show a lightweight skeleton so
  // the user can't click "Save" against an empty form.
  if (!form) {
    return (
      <div className={`fixed inset-0 ${zClass}`}>
        <div onClick={onClose} className="absolute inset-0 bg-zinc-900/20 backdrop-blur-[1px]" />
        <aside className="absolute right-0 top-0 h-full w-[560px] max-w-[100vw] bg-white shadow-2xl ring-1 ring-zinc-200 flex items-center justify-center text-zinc-500 text-[13px] gap-2">
          {err
            ? <span className="text-rose-700">{err}</span>
            : <><Loader2 className="animate-spin" size={16} /> กำลังโหลดข้อมูลบริษัท…</>}
        </aside>
      </div>
    )
  }

  return (
    <div className={`fixed inset-0 ${zClass}`}>
      <div onClick={onClose} className="absolute inset-0 bg-zinc-900/20 backdrop-blur-[1px]" />
      <aside className="absolute right-0 top-0 h-full w-[560px] max-w-[100vw] bg-white shadow-2xl ring-1 ring-zinc-200 flex flex-col">
        <header className="px-5 py-4 border-b border-zinc-100 flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-[11px] uppercase tracking-wider font-medium text-amber-600">
              {isNew ? 'New company' : 'Edit company'}
            </div>
            <div className="flex items-center gap-2 min-w-0">
              <h2 className="text-[15px] font-semibold text-zinc-900 truncate">
                {form.name || <span className="text-zinc-400 font-normal italic">Untitled</span>}
              </h2>
              {!isNew && detail?.operation_count > 0 && (
                <OpsBadge count={detail.operation_count} lastYear={detail.last_operation_year} />
              )}
              {!isNew && detail && (
                <CreatedByChip
                  createdAtUnix={detail.created_at}
                  createdById={detail.created_by_xano_id}
                  fallbackCode={detail.created_by_code}
                />
              )}
            </div>
          </div>
          <button onClick={onClose} aria-label="Close"
            className="w-8 h-8 rounded-lg text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 flex items-center justify-center shrink-0">
            <X size={16} />
          </button>
        </header>

        {!isNew && logoEditorOpen && (
          <LogoEditor
            companyName={form.name}
            initialSrc={logoUrl || null}
            onClose={() => setLogoEditorOpen(false)}
            onSave={saveLogo}
          />
        )}

        <div className="flex-1 overflow-auto px-5 py-4 space-y-4">

          {/* Logo section — only available after the company is saved (we need an
              id to attach the file to). Bigger 80×80 preview + explicit actions. */}
          {!isNew && (
            <CompanyLogoPanel
              name={form.name}
              url={logoUrl}
              hasLogo={!!detail?.logo_updated_at}
              onEdit={() => setLogoEditorOpen(true)}
              onRemove={removeLogo}
            />
          )}

          {/* Company core fields */}
          <Field label="Company name *" icon={Building2} hintInline="short display name">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-2.5 py-1.5 bg-white border border-zinc-200 rounded-lg text-[13.5px] focus:border-amber-500 focus:outline-none" />
          </Field>

          <Field label="Official name" hintInline="for paperwork">
            <input
              value={form.official_company_name}
              onChange={(e) => setForm({ ...form, official_company_name: e.target.value })}
              placeholder="บริษัท ... จำกัด (มหาชน)"
              className="w-full px-2.5 py-1.5 bg-white border border-zinc-200 rounded-lg text-[13.5px] focus:border-amber-500 focus:outline-none placeholder:text-zinc-300"
            />
          </Field>

          <Field label="ชื่อ-นามสกุล ผู้มีอำนาจลงนามของบริษัท" hintInline="ใช้ในเอกสารราชการ">
            <input
              value={form.authorized_signer_name || ''}
              onChange={(e) => setForm({ ...form, authorized_signer_name: e.target.value })}
              placeholder="เช่น นางสาว สมหญิง ใจดี (ตำแหน่งกรรมการ)"
              className="w-full px-2.5 py-1.5 bg-white border border-zinc-200 rounded-lg text-[13.5px] focus:border-amber-500 focus:outline-none placeholder:text-zinc-300"
            />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Tax ID" icon={Hash} hintInline="13 digits">
              <input value={form.tax_id} maxLength={13}
                onChange={(e) => setForm({ ...form, tax_id: e.target.value.replace(/[^0-9]/g, '').slice(0, 13) })}
                className="w-full px-2.5 py-1.5 bg-white border border-zinc-200 rounded-lg text-[13.5px] tabular-nums focus:border-amber-500 focus:outline-none" />
            </Field>
            <Field label="Employees" icon={Users} hintInline="actual count">
              <input type="text" inputMode="numeric" value={form.employee_count}
                onChange={(e) => setForm({ ...form, employee_count: e.target.value.replace(/[^0-9]/g, '') })}
                className="w-full px-2.5 py-1.5 bg-white border border-zinc-200 rounded-lg text-[13.5px] tabular-nums focus:border-amber-500 focus:outline-none" />
            </Field>
          </div>

          <div className={isBangkok(form.province) ? 'grid grid-cols-1 sm:grid-cols-2 gap-3' : ''}>
            <Field label="Province" icon={MapPin}>
              <Combobox
                options={PROVINCES}
                value={form.province || ''}
                placeholder="— select —"
                onChange={(v) => {
                  const province = v || null
                  // Switching away from Bangkok wipes the district (only Bangkok needs it).
                  const district = isBangkok(province) ? form.district : null
                  setForm({
                    ...form,
                    province,
                    district,
                    sso_district: resolveSsoBranch({ province, district }) || '',
                  })
                }}
              />
            </Field>
            {isBangkok(form.province) && (
              <Field label="District (เขต)" icon={MapPin}>
                <Combobox
                  options={BANGKOK_DISTRICTS}
                  value={form.district || ''}
                  placeholder="— select เขต —"
                  onChange={(v) => {
                    const district = v || null
                    setForm({
                      ...form,
                      district,
                      sso_district: resolveSsoBranch({ province: form.province, district }) || '',
                    })
                  }}
                />
              </Field>
            )}
          </div>

          <Field label="Address">
            <textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
              className="w-full px-2.5 py-1.5 bg-white border border-zinc-200 rounded-lg text-[13.5px] focus:border-amber-500 focus:outline-none resize-y" />
          </Field>

          <Field label="Billing Payers" icon={Receipt}>
            <MultiChips options={BILLING_RIGHTS} value={form.billing_rights}
              onChange={(arr) => setForm({ ...form, billing_rights: arr })} />
            <textarea
              rows={2}
              value={form.billing_detail || ''}
              onChange={(e) => setForm({ ...form, billing_detail: e.target.value })}
              placeholder="โน้ตเพิ่มเติม เช่น เบิกสวัสดิการได้ 2,000 บาท / เบิกประกันสังคมตามจริง / etc."
              className="mt-2 w-full px-2.5 py-1.5 bg-white border border-zinc-200 rounded-lg text-[13px] focus:border-amber-500 focus:outline-none resize-y placeholder:text-zinc-400"
            />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="ประกันสังคมเขต" hintInline={isBangkok(form.province) ? 'auto from เขต' : 'เลือกสาขาตามจังหวัด'}>
              <SsoBranchPicker
                province={form.province}
                district={form.district}
                value={form.sso_district || ''}
                onChange={(v) => setForm({ ...form, sso_district: v })}
              />
            </Field>
            <Field label="หมายเลขบัญชีนายจ้าง (ประกันสังคม)">
              <input type="text" value={form.sso_employer_account || ''}
                onChange={(e) => setForm({ ...form, sso_employer_account: e.target.value })}
                placeholder="—"
                className="w-full px-2.5 py-1.5 bg-white border border-zinc-200 rounded-lg text-[13.5px] focus:border-amber-500 focus:outline-none" />
            </Field>
          </div>

          {/* Primary contact — captured during creation; flows into initial_contact */}
          {isNew && (
            <div className="pt-4 border-t border-zinc-100">
              <div className="text-[11px] uppercase tracking-wider font-medium text-zinc-500 mb-2 flex items-center gap-1.5">
                <Star size={11} strokeWidth={2.5} className="text-amber-500 fill-amber-400" />
                Primary contact <span className="text-zinc-400 normal-case tracking-normal font-normal">(optional — saved together with the company)</span>
              </div>
              <div className="space-y-2">
                <input
                  value={form.pc_name}
                  onChange={(e) => setForm({ ...form, pc_name: e.target.value })}
                  placeholder="ชื่อเรียก (จำเป็นถ้าจะแนบ primary contact)"
                  className="w-full px-2.5 py-1.5 bg-white border border-zinc-200 rounded-lg text-[13px] focus:border-amber-500 focus:outline-none"
                />
                <input
                  value={form.pc_fullname || ''}
                  onChange={(e) => setForm({ ...form, pc_fullname: e.target.value })}
                  placeholder="ชื่อ-สกุล (สำหรับเอกสารราชการ)"
                  className="w-full px-2.5 py-1.5 bg-white border border-zinc-200 rounded-lg text-[13px] focus:border-amber-500 focus:outline-none"
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <input
                    value={form.pc_phone}
                    onChange={(e) => setForm({ ...form, pc_phone: e.target.value })}
                    placeholder="Phone"
                    className="w-full px-2.5 py-1.5 bg-white border border-zinc-200 rounded-lg text-[13px] focus:border-amber-500 focus:outline-none"
                  />
                  <input
                    value={form.pc_email}
                    onChange={(e) => setForm({ ...form, pc_email: e.target.value })}
                    placeholder="Email"
                    type="email"
                    className="w-full px-2.5 py-1.5 bg-white border border-zinc-200 rounded-lg text-[13px] focus:border-amber-500 focus:outline-none"
                  />
                  <input
                    value={form.pc_line_id}
                    onChange={(e) => setForm({ ...form, pc_line_id: e.target.value })}
                    placeholder="LINE ID"
                    className="w-full px-2.5 py-1.5 bg-white border border-zinc-200 rounded-lg text-[13px] focus:border-amber-500 focus:outline-none"
                  />
                  <input
                    value={form.pc_role}
                    onChange={(e) => setForm({ ...form, pc_role: e.target.value })}
                    placeholder="Role (e.g. HR, ผู้ประสานงาน)"
                    className="w-full px-2.5 py-1.5 bg-white border border-zinc-200 rounded-lg text-[13px] focus:border-amber-500 focus:outline-none"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Contacts section — only on existing companies (new ones use initial_contact).
              Pass legacy phone/email so the section can show them as fallback when
              no contact_person row exists yet (typical for imported records). */}
          {!isNew && (
            <ContactsSection
              companyId={company.id}
              contacts={detail?.contacts}
              legacyPhone={detail?.phone}
              legacyEmail={detail?.email}
              onChanged={() => api.companies.get(company.id).then(setDetail).catch(() => {})}
            />
          )}

          {/* Operations history (edit mode only) */}
          {!isNew && detail?.operations?.length > 0 && (
            <div className="pt-4 border-t border-zinc-100">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] uppercase tracking-wider font-medium text-zinc-500">
                  Operations
                  <span className="text-zinc-400 normal-case tracking-normal font-normal ml-1.5">
                    ({detail.operations.length})
                  </span>
                </div>
              </div>
              <CompanyOpsList operations={detail.operations} />
            </div>
          )}

          {/* Follow-ups section (edit mode only) */}
          {!isNew && (
            <div className="pt-4 border-t border-zinc-100">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] uppercase tracking-wider font-medium text-zinc-500">
                  Follow-ups
                  {detail && (
                    <span className="text-zinc-400 normal-case tracking-normal font-normal ml-1.5">
                      ({detail.follow_ups?.length || 0})
                    </span>
                  )}
                </div>
                <button
                  onClick={() => onAddFollowUp?.({ id: company.id, name: form.name })}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[12px] font-medium text-amber-700 hover:bg-amber-50"
                >
                  <Plus size={12} strokeWidth={2.5} /> Add follow-up
                </button>
              </div>
              <FollowUpList
                followUps={detail?.follow_ups}
                onPick={(fu) => onEditFollowUp?.(fu, { id: company.id, name: form.name })}
              />
            </div>
          )}

          {err && <div className="px-3 py-2 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-[12.5px]">{err}</div>}
        </div>

        <footer className="px-5 py-3 border-t border-zinc-100 flex items-center justify-between gap-2 bg-zinc-50/60">
          {!isNew ? (
            <button onClick={() => setConfirmOpen(true)} disabled={deleting || saving}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[12.5px] font-medium text-rose-600 hover:bg-rose-50 transition-colors disabled:opacity-50">
              {deleting ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} strokeWidth={2.25} />}
              Delete
            </button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <button onClick={onClose}
              className="px-3 py-1.5 rounded-lg text-[13px] font-medium text-zinc-700 hover:bg-zinc-100">
              Cancel
            </button>
            <button onClick={save} disabled={saving || deleting}
              className="px-3.5 py-1.5 rounded-lg text-[13px] font-semibold bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50 inline-flex items-center gap-1.5 shadow-sm shadow-amber-500/20">
              {saving && <Loader2 size={13} className="animate-spin" />}
              {isNew ? 'Create company' : 'Save changes'}
            </button>
          </div>
        </footer>
      </aside>

      <ConfirmDialog
        open={confirmOpen}
        title="Delete company"
        message={<>This permanently removes <strong>{form?.name || 'this company'}</strong>, all of its contacts, and any follow-ups. Operations linked to it will block the delete.</>}
        confirmLabel="Delete"
        onCancel={() => setConfirmOpen(false)}
        onConfirm={doDelete}
      />
    </div>
  )
}

/* ──────────────────────────────── Contacts section ────────────────────────────────
 *  Multiple contacts per company; at most one primary (enforced by the
 *  uq_contact_primary unique index in D1). Inline edit/add/delete; the
 *  "Make primary" action is shown on every non-primary row so the staff
 *  can promote one at any time.
 * ──────────────────────────────────────────────────────────────────────────────── */

function ContactsSection({ companyId, contacts, legacyPhone, legacyEmail, onChanged }) {
  const [editingId, setEditingId] = useState(null)   // contact id being edited, or 'new' for the add row
  const [busy, setBusy] = useState(false)
  const [err, setErr]   = useState(null)

  const list = contacts || []
  const sorted = [...list].sort((a, b) => (b.is_primary || 0) - (a.is_primary || 0) || a.id - b.id)

  async function setPrimary(id) {
    setErr(null); setBusy(true)
    try {
      await api.companies.contacts.update(companyId, id, { is_primary: true })
      await onChanged()
    } catch (e) { setErr(e.message) }
    finally { setBusy(false) }
  }

  async function remove(id) {
    setErr(null); setBusy(true)
    try {
      await api.companies.contacts.remove(companyId, id)
      if (editingId === id) setEditingId(null)
      await onChanged()
    } catch (e) { setErr(e.message) }
    finally { setBusy(false) }
  }

  async function saveContact(id, payload) {
    setErr(null); setBusy(true)
    try {
      if (id === 'new') await api.companies.contacts.add(companyId, payload)
      else              await api.companies.contacts.update(companyId, id, payload)
      setEditingId(null)
      await onChanged()
    } catch (e) { setErr(e.message); throw e }
    finally { setBusy(false) }
  }

  return (
    <div className="pt-4 border-t border-zinc-100">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase tracking-wider font-medium text-zinc-500">
          Contacts
          {contacts != null && (
            <span className="text-zinc-400 normal-case tracking-normal font-normal ml-1.5">
              ({sorted.length})
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => setEditingId('new')}
          disabled={busy || editingId === 'new'}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[12px] font-medium text-amber-700 hover:bg-amber-50 disabled:opacity-50"
        >
          <Plus size={12} strokeWidth={2.5} /> Add contact
        </button>
      </div>

      {err && <div className="mb-2 px-2.5 py-1.5 rounded-md bg-rose-50 border border-rose-200 text-rose-700 text-[12px]">{err}</div>}

      {contacts == null ? (
        <div className="text-[12.5px] text-zinc-400 py-4 text-center">Loading…</div>
      ) : sorted.length === 0 && editingId !== 'new' ? (
        (legacyPhone || legacyEmail) ? (
          <div className="rounded-xl bg-zinc-50/70 ring-1 ring-zinc-100 p-3 space-y-1.5">
            <div className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500">
              Imported contact info
              <span className="ml-1 text-zinc-400 normal-case tracking-normal font-normal">— no contact record yet</span>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12.5px] text-zinc-700">
              {legacyPhone && <a href={`tel:${legacyPhone}`} className="inline-flex items-center gap-1 hover:text-zinc-900 tabular-nums"><Phone size={12} /> {legacyPhone}</a>}
              {legacyEmail && <a href={`mailto:${legacyEmail}`} className="inline-flex items-center gap-1 hover:text-zinc-900 truncate"><Mail size={12} /> {legacyEmail}</a>}
            </div>
            <p className="text-[11px] text-zinc-500">Click "Add contact" above to create a proper contact record from this data.</p>
          </div>
        ) : (
          <div className="text-[12.5px] text-zinc-400 py-4 text-center">No contacts yet — click "Add contact" to start.</div>
        )
      ) : (
        <ul className="space-y-1.5">
          {sorted.map((c) =>
            editingId === c.id ? (
              <ContactEditForm
                key={c.id}
                initial={c}
                lockPrimary={sorted.length === 1}
                onCancel={() => setEditingId(null)}
                onSubmit={(payload) => saveContact(c.id, payload)}
              />
            ) : (
              <ContactRow
                key={c.id}
                contact={c}
                onlyOne={sorted.length === 1}
                disabled={busy}
                onEdit={() => setEditingId(c.id)}
                onSetPrimary={() => setPrimary(c.id)}
                onRemove={() => remove(c.id)}
              />
            )
          )}
          {editingId === 'new' && (
            <ContactEditForm
              isNew
              onCancel={() => setEditingId(null)}
              onSubmit={(payload) => saveContact('new', payload)}
            />
          )}
        </ul>
      )}
    </div>
  )
}

function ContactLabeledInput({ label, hint, required, autoFocus, type = 'text', value, onChange, placeholder }) {
  return (
    <label className="block">
      <span className="text-[11px] font-medium text-zinc-600 flex items-center gap-1.5">
        {label}
        {required && <span className="text-amber-600 font-bold">*</span>}
        {hint && <span className="text-[10.5px] text-zinc-400 font-normal">({hint})</span>}
      </span>
      <input
        type={type} value={value || ''} autoFocus={autoFocus} required={required}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-0.5 w-full px-2.5 py-1.5 bg-white border border-zinc-200 rounded-lg text-[13px] focus:border-amber-500 focus:outline-none placeholder:text-zinc-300"
      />
    </label>
  )
}

function ContactRow({ contact, onlyOne, disabled, onEdit, onSetPrimary, onRemove }) {
  const [confirmOpen, setConfirmOpen] = useState(false)
  const primary = !!contact.is_primary
  // Display rule:
  //  - Primary contact → full info (phone / email / line / role)
  //  - Non-primary     → just the name (keeps the list scannable)
  const showDetails = primary
  return (
    <>
      <li className={`group rounded-xl p-2.5 ring-1 ${primary ? 'bg-amber-50/50 ring-amber-200/70' : 'bg-zinc-50/70 ring-zinc-100'}`}>
        <div className="flex items-start gap-2.5">
          <UserCircle2 size={18} className={`mt-0.5 shrink-0 ${primary ? 'text-amber-600' : 'text-zinc-400'}`} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[13px] font-medium text-zinc-900 truncate" title={contact.fullname || contact.name}>{contact.name}</span>
              {primary && (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold bg-amber-100 text-amber-800 ring-1 ring-amber-200/80">
                  <Star size={9} strokeWidth={2.5} className="fill-current" />
                  Primary
                </span>
              )}
              {showDetails && contact.role && (
                <span className="inline-flex items-center gap-1 text-[11px] text-zinc-500">
                  <Briefcase size={10} strokeWidth={2} />
                  {contact.role}
                </span>
              )}
            </div>
            {showDetails && contact.fullname && (
              <div className="mt-0.5 text-[11.5px] text-zinc-500 truncate" title="ชื่อ-สกุล สำหรับเอกสาร">
                {contact.fullname}
              </div>
            )}
            {showDetails && (
              <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11.5px] text-zinc-500">
                {contact.phone && <a href={`tel:${contact.phone}`} className="inline-flex items-center gap-1 hover:text-zinc-900 tabular-nums" onClick={e => e.stopPropagation()}><Phone size={10} /> {contact.phone}</a>}
                {contact.email && <a href={`mailto:${contact.email}`} className="inline-flex items-center gap-1 hover:text-zinc-900 truncate" onClick={e => e.stopPropagation()}><Mail size={10} /> {contact.email}</a>}
                {contact.line_id && <span className="inline-flex items-center gap-1"><MessageCircle size={10} /> {contact.line_id}</span>}
              </div>
            )}
          </div>
          <div className="flex items-center gap-0.5 shrink-0">
            {/* "Make primary" hidden when this row is already primary OR when there's only one row (locked-primary rule) */}
            {!primary && !onlyOne && (
              <button
                type="button"
                onClick={onSetPrimary}
                disabled={disabled}
                title="Set as primary"
                className="px-1.5 py-1 rounded-md text-[11px] text-zinc-500 hover:text-amber-700 hover:bg-amber-50 disabled:opacity-50 transition-colors"
              >
                <Star size={13} strokeWidth={2.25} />
              </button>
            )}
            <button
              type="button"
              onClick={onEdit}
              disabled={disabled}
              title="Edit"
              className="p-1 rounded-md text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 disabled:opacity-50 transition-colors"
            >
              <Pencil size={13} strokeWidth={2.25} />
            </button>
            <button
              type="button"
              onClick={() => setConfirmOpen(true)}
              disabled={disabled}
              title="Delete"
              className="p-1 rounded-md text-zinc-500 hover:text-rose-600 hover:bg-rose-50 disabled:opacity-50 transition-colors"
            >
              <Trash2 size={13} strokeWidth={2.25} />
            </button>
          </div>
        </div>
      </li>
      <ConfirmDialog
        open={confirmOpen}
        title="Delete contact"
        message={<>Delete <strong>{contact.name}</strong>? This action can&apos;t be undone.</>}
        confirmLabel="Delete"
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => { setConfirmOpen(false); onRemove() }}
      />
    </>
  )
}

function ContactEditForm({ initial, isNew = false, lockPrimary = false, onCancel, onSubmit }) {
  const [form, setForm] = useState({
    name:     initial?.name     || '',
    fullname: initial?.fullname || '',
    phone:    initial?.phone    || '',
    email:    initial?.email    || '',
    line_id:  initial?.line_id  || '',
    role:     initial?.role     || '',
    // When this is the only contact, primary is forced on regardless of stored value.
    is_primary: lockPrimary ? true : (initial?.is_primary === 1 || initial?.is_primary === true),
  })
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)

  async function submit(e) {
    e.preventDefault()
    if (!form.name.trim()) { setErr('Name is required.'); return }
    // Phone is intentionally free-form text — no validation.
    const validationErr = firstError(
      validateEmail(form.email, 'Email'),
    )
    if (validationErr) { setErr(validationErr); return }
    setErr(null); setSaving(true)
    try { await onSubmit({ ...form, name: form.name.trim(), fullname: form.fullname.trim() }) }
    catch (e) { setErr(e.message) }
    finally { setSaving(false) }
  }

  return (
    <li className="rounded-xl p-3 bg-white ring-1 ring-amber-200/80 shadow-sm shadow-amber-500/5">
      <form onSubmit={submit} className="space-y-2.5">
        <div className="text-[10.5px] uppercase tracking-wider font-semibold text-amber-700">
          {isNew ? 'New contact' : 'Edit contact'}
        </div>
        <ContactLabeledInput label="ชื่อเรียก" required autoFocus value={form.name}
          onChange={(v) => setForm({ ...form, name: v })}
          placeholder="เช่น เปิ้ล / อิ้ง" />
        <ContactLabeledInput label="ชื่อ-สกุล" hint="สำหรับเอกสารราชการ"
          value={form.fullname} onChange={(v) => setForm({ ...form, fullname: v })}
          placeholder="เช่น นางสาว สมหญิง ใจดี" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          <ContactLabeledInput label="เบอร์โทร" value={form.phone}
            onChange={(v) => setForm({ ...form, phone: v })} placeholder="—" />
          <ContactLabeledInput label="อีเมล" type="email" value={form.email}
            onChange={(v) => setForm({ ...form, email: v })} placeholder="—" />
          <ContactLabeledInput label="LINE ID" value={form.line_id}
            onChange={(v) => setForm({ ...form, line_id: v })} placeholder="—" />
          <ContactLabeledInput label="ตำแหน่ง" value={form.role}
            onChange={(v) => setForm({ ...form, role: v })} placeholder="เช่น HR, ผู้ประสานงาน" />
        </div>
        <label className={`flex items-center gap-2 text-[12.5px] select-none ${lockPrimary ? 'text-zinc-400 cursor-not-allowed' : 'text-zinc-700 cursor-pointer'}`}>
          <input
            type="checkbox"
            checked={form.is_primary}
            disabled={lockPrimary}
            onChange={e => setForm({ ...form, is_primary: e.target.checked })}
            className="accent-amber-500 w-3.5 h-3.5 disabled:opacity-60"
          />
          <span>Set as primary contact</span>
          {lockPrimary
            ? <span className="text-[11px] text-zinc-400">(locked — this is the only contact)</span>
            : form.is_primary && (
                <span className="text-[11px] text-amber-700 inline-flex items-center gap-0.5">
                  <Star size={10} className="fill-current" /> demotes any current primary
                </span>
              )}
        </label>
        {err && <div className="px-2.5 py-1.5 rounded-md bg-rose-50 border border-rose-200 text-rose-700 text-[12px]">{err}</div>}
        <div className="flex items-center justify-end gap-2 pt-1">
          <button type="button" onClick={onCancel} disabled={saving}
            className="px-2.5 py-1 rounded-md text-[12.5px] text-zinc-600 hover:bg-zinc-100">
            Cancel
          </button>
          <button type="submit" disabled={saving}
            className="px-3 py-1 rounded-md text-[12.5px] font-semibold bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50 inline-flex items-center gap-1.5">
            {saving && <Loader2 size={12} className="animate-spin" />}
            <Check size={12} strokeWidth={2.5} />
            {isNew ? 'Add' : 'Save'}
          </button>
        </div>
      </form>
    </li>
  )
}


/* Small "we've serviced this company N time(s), last in YYYY" badge.
   Amber to stay in the brand palette and stand out next to the company name. */
function OpsBadge({ count, lastYear }) {
  if (!count) return null
  const yearTH = lastYear ? lastYear + 543 : null  // CE → Buddhist Era for display
  return (
    <span
      title={`บริการแล้ว ${count} ครั้ง${yearTH ? ` · ล่าสุด พ.ศ. ${yearTH}` : ''}`}
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-amber-50 ring-1 ring-amber-200/70 text-[11px] font-medium text-amber-800 tabular-nums shrink-0"
    >
      <Truck size={10} strokeWidth={2.5} />
      {count}
      {yearTH && <span className="text-amber-600 font-normal">· {yearTH}</span>}
    </span>
  )
}

// Optional seed = fields that other parts of the app want to pre-fill (e.g.
// the lead → company flow seeds pc_* from the lead's contact info).
function emptyForm(seed = {}) {
  return {
    name:                  seed.name || '',
    official_company_name: seed.official_company_name || '',
    authorized_signer_name: seed.authorized_signer_name || '',
    tax_id:                seed.tax_id || '',
    address:               seed.address || '',
    province:              seed.province || '',
    district:              seed.district || null,
    employee_count:        seed.employee_count ?? '',
    billing_rights:        Array.isArray(seed.billing_rights) ? seed.billing_rights : [],
    billing_detail:        seed.billing_detail || '',
    sso_district:          seed.sso_district || '',
    sso_employer_account:  seed.sso_employer_account || '',
    used_before:           seed.used_before ?? null,
    // Primary contact captured at company-creation time (sent as initial_contact)
    pc_name:     seed.pc_name     || '',
    pc_fullname: seed.pc_fullname || '',
    pc_phone:    seed.pc_phone    || '',
    pc_email:   seed.pc_email   || '',
    pc_line_id: seed.pc_line_id || '',
    pc_role:    seed.pc_role    || '',
  }
}
function formFromCompany(c) {
  const province = c.province || ''
  const district = c.district || null
  // Prefer the SAVED sso_district. Only fall back to the auto-derived branch
  // (resolveSsoBranch returns the FIRST option for multi-branch provinces) for
  // rows that pre-date this field — otherwise the user's manual choice gets
  // silently overwritten by the default on every drawer re-open.
  const ssoDerived = resolveSsoBranch({ province, district })
  return {
    name:                  c.name || '',
    official_company_name: c.official_company_name || '',
    authorized_signer_name: c.authorized_signer_name || '',
    tax_id:                c.tax_id || '',
    address:               c.address || '',
    province,
    district,
    employee_count:        c.employee_count ?? '',
    billing_rights:        c.billing_rights ? (typeof c.billing_rights === 'string' ? JSON.parse(c.billing_rights) : c.billing_rights) : [],
    billing_detail:        c.billing_detail || '',
    sso_district:          c.sso_district || ssoDerived || '',
    sso_employer_account:  c.sso_employer_account || '',
    used_before:           c.used_before ?? null,
  }
}


/* ──────────────────────────────── Follow-up list inside company drawer ──────────────────────────────── */

function FollowUpList({ followUps, onPick }) {
  if (followUps == null) {
    return <div className="text-[12.5px] text-zinc-400 py-4 text-center">Loading…</div>
  }
  if (followUps.length === 0) {
    return (
      <div className="px-3 py-6 rounded-xl border border-dashed border-zinc-200 text-center text-[12.5px] text-zinc-400">
        No follow-ups yet for this company.
      </div>
    )
  }
  return (
    <ul className="space-y-1.5">
      {followUps.map((f) => (
        <li key={f.id}>
          <button
            onClick={() => onPick?.(f)}
            className="w-full text-left px-3 py-2 rounded-lg border border-zinc-200 hover:border-amber-300 hover:bg-amber-50/30 transition-colors"
          >
            <div className="flex items-center justify-between gap-2 mb-0.5">
              <span className="text-[12px] font-semibold tabular-nums text-zinc-700">{fmtDate(f.date)}</span>
              <div className="flex items-center gap-1.5">
                {f.channel && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-zinc-50 ring-1 ring-zinc-200/70 text-[10.5px] font-medium text-zinc-700">
                    <span className={`w-1 h-1 rounded-full ${CHANNEL_DOT[f.channel] || 'bg-zinc-400'}`} />
                    {f.channel}
                  </span>
                )}
                {f.outcome_stage && (
                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ring-1 text-[10.5px] font-medium ${STAGE_BADGE[f.outcome_stage] || 'bg-zinc-50 ring-zinc-200/70 text-zinc-700'}`}>
                    <span className={`w-1 h-1 rounded-full ${STAGE_DOT[f.outcome_stage] || 'bg-zinc-400'}`} />
                    {f.outcome_stage}
                  </span>
                )}
              </div>
            </div>
            <div className="text-[12.5px] text-zinc-600 line-clamp-2">
              {f.note || <span className="text-zinc-300 italic">(no note)</span>}
            </div>
          </button>
        </li>
      ))}
    </ul>
  )
}

function fmtDate(unix) {
  if (!unix) return '—'
  const d = new Date(unix * 1000)
  return d.toLocaleDateString('th-TH-u-ca-gregory', { day: '2-digit', month: 'short', year: 'numeric' }) +
    ' · ' + d.toLocaleTimeString('th-TH-u-ca-gregory', { hour: '2-digit', minute: '2-digit' })
}


/* ───────────────────────────── Company ops history ─────────────────────── */

/**
 * Per-company past operations panel for inside CompanyDrawer. Each row shows
 * the stage badge + scheduled date and links to /operations/:id when clicked.
 */
function CompanyOpsList({ operations }) {
  const navigate = useNavigate()
  return (
    <ul className="space-y-1.5">
      {operations.map((o) => (
        <li key={o.id}>
          <button
            type="button"
            onClick={() => navigate(`/operations/${o.id}`)}
            className="w-full text-left flex items-center gap-2.5 px-3 py-2 rounded-lg ring-1 ring-zinc-200/70 hover:ring-amber-300 hover:bg-amber-50/40 transition-colors"
          >
            <CalendarClock size={14} className="text-amber-500 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-[12.5px] text-zinc-900">
                <span className="tabular-nums font-medium">{fmtOpDate(o.start_date, o.end_date)}</span>
                {o.expected_patients ? <span className="text-zinc-500">· N {o.expected_patients}</span> : null}
              </div>
              <div className="mt-0.5">
                <OpStageBadge stage={o.stage} />
              </div>
            </div>
            <ChevronRight size={13} className="text-zinc-400 shrink-0" />
          </button>
        </li>
      ))}
    </ul>
  )
}

const OP_STAGE_BADGE = {
  draft:              'bg-zinc-50 ring-zinc-200/70 text-zinc-700',
  'ระหว่างดำเนินการ': 'bg-amber-50 ring-amber-200/80 text-amber-900',
  confirmed:          'bg-blue-50 ring-blue-200/80 text-blue-800',
  complete:           'bg-emerald-50 ring-emerald-200/80 text-emerald-800',
  cancelled:          'bg-rose-50 ring-rose-200/80 text-rose-800',
}
function OpStageBadge({ stage }) {
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10.5px] font-medium ring-1 ${OP_STAGE_BADGE[stage] || 'bg-zinc-50 ring-zinc-200/70 text-zinc-700'}`}>
      {stage}
    </span>
  )
}

function fmtOpDate(startKey, endKey) {
  if (!startKey) return '—'
  const sStr = fmtDateOnlyKey(startKey)
  if (!endKey || endKey === startKey) return sStr
  return sStr + ' → ' + fmtDateOnlyKey(endKey).replace(/\s\d{4}$/, '')
}
function fmtDateOnlyKey(key) {
  const [y, m, d] = key.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d, 12)).toLocaleDateString('th-TH-u-ca-gregory', { day: '2-digit', month: 'short', year: 'numeric' })
}


/* ───────────────────────────── SSO branch picker ─────────────────────────
 *  For Bangkok: shows the auto-derived branch (read-only — driven by district).
 *  For other provinces: dropdown of built-in + user-added branches, with an
 *  inline "+ เพิ่มสาขา" affordance. User additions persist in localStorage and
 *  are scoped to the chosen province.
 * ──────────────────────────────────────────────────────────────────────────── */
function SsoBranchPicker({ province, district, value, onChange }) {
  const [open, setOpen]       = useState(false)
  const [adding, setAdding]   = useState(false)
  const [draft, setDraft]     = useState('')
  const [version, setVersion] = useState(0)   // bump to re-pull customBranchesFor after add/remove
  const ref = useRef(null)

  useEffect(() => {
    if (!open) return
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setAdding(false); setDraft('') } }
    const onEsc = (e) => { if (e.key === 'Escape') { setOpen(false); setAdding(false); setDraft('') } }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onEsc)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onEsc)
    }
  }, [open])

  // Bangkok stays driven by the district picker — show the auto-resolved value
  // as a read-only chip so the user understands it's derived.
  if (isBangkok(province)) {
    const resolved = resolveSsoBranch({ province, district })
    return (
      <input
        value={resolved || value || ''}
        readOnly
        placeholder={district ? '—' : 'เลือกเขตเพื่อระบุสำนักงาน'}
        className="w-full px-2.5 py-1.5 bg-zinc-50 border border-zinc-200 rounded-lg text-[13.5px] text-zinc-700 placeholder:text-zinc-400 cursor-not-allowed"
      />
    )
  }

  if (!province) {
    return (
      <input value="" readOnly placeholder="— เลือก Province —"
        className="w-full px-2.5 py-1.5 bg-zinc-50 border border-zinc-200 rounded-lg text-[13.5px] text-zinc-400 cursor-not-allowed" />
    )
  }

  const options = ssoOptionsForProvince(province)
  const customSet = new Set(customBranchesFor(province))    // tracked via `version`; eslint-disable-next-line
  void version

  async function commitAdd() {
    const v = draft.trim()
    if (!v) { setAdding(false); return }
    addCustomBranch(province, v)
    setAdding(false); setDraft('')
    setVersion((n) => n + 1)
    onChange(v)
  }
  function removeBranch(b, e) {
    e.stopPropagation()
    removeCustomBranch(province, b)
    setVersion((n) => n + 1)
    if (value === b) onChange(options.find((o) => o !== b) || '')
  }

  return (
    <div ref={ref} className="relative">
      <button type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full px-2.5 py-1.5 bg-white border border-zinc-200 rounded-lg text-[13.5px] text-left flex items-center justify-between gap-2 hover:border-zinc-300 focus:border-amber-500 focus:outline-none">
        <span className={value ? 'text-zinc-900 truncate' : 'text-zinc-400'}>
          {value || '— เลือกสำนักงาน —'}
        </span>
        <ChevronDown size={13} className={'shrink-0 text-zinc-400 transition-transform ' + (open ? 'rotate-180' : '')} />
      </button>
      {open && (
        <div className="absolute left-0 right-0 top-full mt-1 z-30 bg-white rounded-xl ring-1 ring-zinc-200 shadow-[0_10px_32px_-8px_rgba(0,0,0,0.18)] p-1.5 max-h-[280px] overflow-y-auto">
          <ul className="space-y-0.5">
            {options.map((opt) => {
              const isCustom = customSet.has(opt)
              const isSelected = opt === value
              return (
                <li key={opt} className="flex items-center gap-1">
                  <button type="button"
                    onClick={() => { onChange(opt); setOpen(false) }}
                    className={'flex-1 text-left px-2 py-1.5 rounded-md text-[12.5px] flex items-center justify-between gap-2 ' +
                      (isSelected ? 'bg-amber-50 text-amber-900 font-medium' : 'text-zinc-700 hover:bg-amber-50')}>
                    <span className="truncate">{opt}</span>
                    {isSelected && <Check size={11} className="text-amber-600 shrink-0" />}
                  </button>
                  {isCustom && (
                    <button type="button" onClick={(e) => removeBranch(opt, e)}
                      title="ลบสาขาที่เพิ่มเอง"
                      className="w-7 h-7 rounded-md text-zinc-400 hover:text-rose-600 hover:bg-rose-50 inline-flex items-center justify-center">
                      <X size={11} />
                    </button>
                  )}
                </li>
              )
            })}
          </ul>
          <div className="mt-1 pt-1 border-t border-zinc-100">
            {adding ? (
              <div className="flex items-center gap-1.5 px-1.5 py-1">
                <input value={draft} autoFocus maxLength={200}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitAdd() } }}
                  placeholder={`สาขาใหม่ใน ${province}`}
                  className="flex-1 px-2 py-1 rounded-md ring-1 ring-zinc-200 focus:ring-2 focus:ring-amber-400 focus:outline-none text-[12.5px]" />
                <button type="button" onClick={commitAdd}
                  className="px-2 py-1 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-[11.5px] font-medium">เพิ่ม</button>
                <button type="button" onClick={() => { setAdding(false); setDraft('') }}
                  className="text-[11.5px] text-zinc-500 hover:text-zinc-900 px-1">ยกเลิก</button>
              </div>
            ) : (
              <button type="button" onClick={() => setAdding(true)}
                className="w-full inline-flex items-center gap-1.5 px-2 py-1.5 rounded-md text-amber-800 hover:bg-amber-50 text-[12.5px] font-medium">
                <Plus size={11} /> เพิ่มสาขาใหม่
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}


/* ───────────────────────────── Logo avatar + button ─────────────────────── */

/**
 * Square logo avatar suitable for drawer/list use. Falls back to initial-on-amber.
 * `size` is in CSS pixels (rendered as Tailwind w-N/h-N for common sizes).
 */
export function CompanyLogoAvatar({ name, url, size = 44 }) {
  if (url) {
    return (
      <img src={url} alt="" loading="lazy"
        className="rounded-xl object-cover ring-1 ring-zinc-200 shrink-0 bg-white"
        style={{ width: size, height: size }}
      />
    )
  }
  return (
    <div
      className="rounded-xl bg-amber-50 ring-1 ring-amber-200 text-amber-700 inline-flex items-center justify-center font-bold shrink-0 select-none"
      style={{ width: size, height: size, fontSize: Math.max(11, Math.floor(size * 0.42)) }}
      title={name || ''}
    >
      {firstInitial(name)}
    </div>
  )
}

/**
 * Pick a single-character "initial" for a logo-fallback chip.
 *
 *   - For Thai names: skip pre-vowels (เ แ โ ใ ไ) and combining marks/tones
 *     (ิ ี ึ ื ุ ู ำ ่ ้ ๊ ๋ ์ ัฺ etc.) and return the first พยัญชนะ (consonant).
 *     So "เอกชน" → "อ", "ไทย" → "ท", "พริ้ตตี้" → "พ".
 *   - For Latin names: return the first letter (case preserved).
 *   - For names that start with a digit/symbol but contain a letter later:
 *     return the first letter found.
 *   - Falls back to '?'.
 */
function firstInitial(s) {
  if (!s) return '?'
  const trimmed = String(s).trim()
  for (const ch of trimmed) {
    const cp = ch.codePointAt(0)
    if (cp >= 0x0E01 && cp <= 0x0E2E) return ch          // Thai consonants ก..ฮ
    if (cp >= 0x0041 && cp <= 0x005A) return ch          // A-Z
    if (cp >= 0x0061 && cp <= 0x007A) return ch          // a-z
  }
  // Nothing matched (e.g. all digits) — fall back to the very first non-space char.
  return [...trimmed][0] || '?'
}

/**
 * Dedicated logo panel inside CompanyDrawer body — 80×80 preview with
 * primary "อัปโหลด / แก้ไข" action and a secondary "ลบ" link.
 */
function CompanyLogoPanel({ name, url, hasLogo, onEdit, onRemove }) {
  return (
    <div className="rounded-2xl ring-1 ring-amber-100/70 bg-gradient-to-br from-amber-50/40 to-orange-50/20 px-4 py-3.5 flex items-center gap-4">
      <CompanyLogoAvatar name={name} url={url} size={80} />
      <div className="min-w-0 flex-1">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-amber-700">โลโก้บริษัท</div>
        <p className="text-[12px] text-zinc-600 mt-0.5 leading-snug">
          {hasLogo
            ? 'ใช้แสดงในหน้าจอ + เอกสาร เช่น HR report และภาพ PR'
            : 'อัปโหลดเพื่อแสดงในหน้าจอ + เอกสาร · JPG/PNG · ครอป 1:1 บันทึก 500×500'}
        </p>
        <div className="mt-2 flex items-center gap-3">
          <button
            type="button"
            onClick={onEdit}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-[12.5px] font-medium"
          >
            {hasLogo ? <><Pencil size={12} /> แก้ไขโลโก้</> : <><Upload size={12} /> อัปโหลดโลโก้</>}
          </button>
          {hasLogo && (
            <button
              type="button"
              onClick={onRemove}
              className="text-[12px] text-zinc-500 hover:text-rose-600 inline-flex items-center gap-1"
            >
              <Trash2 size={11} /> ลบโลโก้
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
